import { useState } from "react";
import { FileUser, Loader2 } from "lucide-react";
import * as XLSX from "xlsx";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/lib/supabase/client";
import type { ProfileMetadata } from "@/lib/supabase/types";
import { REVELATIO_TEAM_ID } from "@/lib/auth";

interface ProfileRow {
  id: string;
  email: string;
  metadata: ProfileMetadata | null;
}

interface UserTeamRow {
  user_id: string;
  team_id: string;
  teams: { name: string } | { name: string }[] | null;
}

function errorMessage(err: unknown): string {
  if (err instanceof Error) return err.message;
  if (
    err &&
    typeof err === "object" &&
    "message" in err &&
    typeof (err as { message: unknown }).message === "string"
  ) {
    return (err as { message: string }).message;
  }
  return "Não foi possível gerar o arquivo XLSX.";
}

function teamName(
  teams: UserTeamRow["teams"]
): string | null {
  if (!teams) return null;
  const data = Array.isArray(teams) ? teams[0] : teams;
  return data?.name ?? null;
}

export function ExportUsersButton() {
  const [exporting, setExporting] = useState(false);
  const { toast } = useToast();

  const handleExport = async () => {
    setExporting(true);
    try {
      const [{ data: profiles, error: profilesError }, { data: teamUsers, error: teamsError }] =
        await Promise.all([
          supabase
            .from("profiles")
            .select("id, email, metadata")
            .order("email"),
          supabase.from("user_teams").select("user_id, team_id, teams ( name )"),
        ]);

      if (profilesError) throw profilesError;
      if (teamsError) throw teamsError;

      const teamsByUser = new Map<string, string[]>();
      const revelatioUsers = new Set<string>();
      for (const row of (teamUsers ?? []) as UserTeamRow[]) {
        if (row.team_id === REVELATIO_TEAM_ID) {
          revelatioUsers.add(row.user_id);
          continue;
        }
        const name = teamName(row.teams);
        if (!name) continue;
        const existing = teamsByUser.get(row.user_id) ?? [];
        if (!existing.includes(name)) {
          existing.push(name);
          teamsByUser.set(row.user_id, existing);
        }
      }

      const rows = ((profiles ?? []) as ProfileRow[])
        .map((profile) => {
          const visibleTeams = teamsByUser.get(profile.id) ?? [];

          const onlyRevelatio =
            revelatioUsers.has(profile.id) && visibleTeams.length === 0;

          if (onlyRevelatio) return null;

          return {
            nome: profile.metadata?.name?.trim() || "Usuário sem nome",
            email: profile.email,
            equipes: [...visibleTeams].sort((a, b) => a.localeCompare(b, "pt-BR")).join(", "),
          };
        })
        .filter((row): row is NonNullable<typeof row> => row !== null);

      const worksheet = XLSX.utils.json_to_sheet(rows);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "usuarios");
      XLSX.writeFile(workbook, `usuarios_${Date.now()}.xlsx`);
    } catch (err: unknown) {
      console.error("Error exporting users:", err);
      toast({
        title: "Erro ao exportar",
        description: errorMessage(err),
        variant: "destructive",
      });
    } finally {
      setExporting(false);
    }
  };

  return (
    <Button
      variant="ghost"
      size="icon"
      className="rounded-full bg-secondary hover:bg-accent"
      onClick={handleExport}
      disabled={exporting}
      title="Exportar usuários (XLSX)"
      aria-label="Exportar usuários (XLSX)"
    >
      {exporting ? (
        <Loader2 className="h-5 w-5 animate-spin" />
      ) : (
        <FileUser className="h-5 w-5" />
      )}
    </Button>
  );
}
